import { GameStateEnums } from '../../../../../../shared/GameEnums.ts';
import { UIGameMapHighlightController } from './UIGameMapHighlightController.ts';
import { ArrayUtils } from '@colonist/utils';
import { PIXI } from '@colonist/pixi';
import { UIGameSpriteHandler } from '../../../UIGameSpriteHandler.ts';
import { ActionState, TileEdgeDirection, TurnState } from '@colonist/game-states';
const UIGameEventEnums = GameStateEnums.UIGameEventEnums;
export class UIGameMapTileEdgeHighlightController extends UIGameMapHighlightController {
    onEvent(data, eventType) {
        super.onEvent(data, eventType);
        switch(eventType){
            case UIGameEventEnums.SetRoadEdgeHighlight:
                this.setRoadEdges(data);
                break;
            case UIGameEventEnums.SetShipEdgeHighlight:
                this.setShipEdges(data);
                break;
            case UIGameEventEnums.BeginnerModeActionUpdated:
                this.onBeginnerModeActionUpdate(data);
                break;
            default:
                break;
        }
    }
    setRoadEdges(highlightedIndexes) {
        const previousRoadEdges = [
            ...this.roadEdges
        ];
        const previousShipEdges = [
            ...this.shipEdges
        ];
        this.roadEdges = highlightedIndexes;
        this.updateHighlightsDifferentially(previousRoadEdges, previousShipEdges, this.confirmActionController.selectedLocationIndex);
    }
    setShipEdges(highlightedIndexes) {
        const previousRoadEdges = [
            ...this.roadEdges
        ];
        const previousShipEdges = [
            ...this.shipEdges
        ];
        this.shipEdges = highlightedIndexes;
        this.updateHighlightsDifferentially(previousRoadEdges, previousShipEdges, this.confirmActionController.selectedLocationIndex);
    }
    updateHighlightsDifferentially(previousRoadEdges, previousShipEdges, selectedIndex) {
        const roadEdgesToRemove = previousRoadEdges.filter((edge)=>!this.roadEdges.includes(edge));
        const shipEdgesToRemove = previousShipEdges.filter((edge)=>!this.shipEdges.includes(edge));
        const roadEdgesToAdd = this.roadEdges.filter((edge)=>!previousRoadEdges.includes(edge));
        const shipEdgesToAdd = this.shipEdges.filter((edge)=>!previousShipEdges.includes(edge));
        this.removeSelectedIndexHighlights([
            ...roadEdgesToRemove,
            ...shipEdgesToRemove
        ]);
        const isSelectedIndexNoLongerValid = selectedIndex !== undefined && !(this.roadEdges.includes(selectedIndex) || this.shipEdges.includes(selectedIndex));
        if (isSelectedIndexNoLongerValid) this.confirmActionController.cleanupConfirmation();
        if (this.roadEdges.length > 0 || this.shipEdges.length > 0) {
            this.confirmActionController.setupInvisibleBackground(()=>{
                this.startAnimation();
                this.hideBeginnerHintInformation();
            });
        } else {
            this.confirmActionController.cleanupConfirmation();
        }
        this.addHighlightsForEdges(roadEdgesToAdd, shipEdgesToAdd);
        if (this.highlightViews.length > 0) {
            this.uiGameManager.mapController.mapView.sortChildren();
            this.startAnimation();
        }
    }
    addHighlightsForEdges(roadEdgesToAdd, shipEdgesToAdd) {
        for (const edgeIndex of roadEdgesToAdd){
            this.highlightEdge(this.uiGameManager.gameState.mapState.tileState.tileEdges[edgeIndex].hexEdge, edgeIndex);
        }
        for (const edgeIndex of shipEdgesToAdd){
            if (!this.roadEdges.includes(edgeIndex)) {
                this.highlightEdge(this.uiGameManager.gameState.mapState.tileState.tileEdges[edgeIndex].hexEdge, edgeIndex);
            }
        }
    }
    getActionForEdgeHighlight(highlightParameters, index) {
        return ()=>{
            if (!this.isAllowedPlacementIndex(index)) return this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.BeginnerModeForceOpenGuideBubble);
            const confirmationParameters = this.getConfirmationParameters(index);
            const skipConfirmation = this.uiGameManager.gameController.currentState.turnState !== TurnState.InitialPlacement && confirmationParameters.length <= 1;
            if (skipConfirmation) return this.confirmedEdgeSelection(index);
            const buttonSize = highlightParameters.size * 1.75 * this.buttonScaleForMobile;
            const highlightCenter = new PIXI.Point(highlightParameters.x + highlightParameters.size / 2, highlightParameters.y + highlightParameters.size / 2);
            const buttonX = highlightCenter.x - buttonSize / 2;
            const buttonY = highlightCenter.y - buttonSize * 1.5;
            const cancelAction = ()=>this.startAnimation();
            this.destroyBeginnerHintView();
            this.endAnimation();
            this.confirmActionController.showActionConfirmation(highlightParameters, buttonX, buttonY, buttonSize, cancelAction, confirmationParameters, index);
        };
    }
    getEdgeHighlightParameters(edge, edgeIndex) {
        const endPoint1 = edge.endPoints()[0].toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2);
        const endPoint2 = edge.endPoints()[1].toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2);
        const size = Math.sqrt((endPoint1.x - endPoint2.x) * (endPoint1.x - endPoint2.x) + (endPoint1.y - endPoint2.y) * (endPoint1.y - endPoint2.y)) / 2 * this.circlesScaleForMobile;
        const centerX = (endPoint1.x + endPoint2.x) / 2;
        const centerY = (endPoint1.y + endPoint2.y) / 2;
        const highlight = {
            x: centerX - size / 2,
            y: centerY - size / 2,
            size: size,
            index: edgeIndex
        };
        return highlight;
    }
    confirmedEdgeSelection(index) {
        switch(this.uiGameManager.gameController.myActionState){
            case ActionState.PlaceRoad:
            case ActionState.PlaceRoadForFree:
                this.uiGameManager.socketGameSend.confirmBuildRoad(index);
                break;
            case ActionState.InitialPlacementRoadPlacement:
                this.uiGameManager.socketGameSend.confirmBuildRoad(index);
                this.uiGameManager.socketGameSend.confirmBuildShip(index);
                break;
            case ActionState.Place2MoreRoadBuilding:
            case ActionState.Place1MoreRoadBuilding:
                this.uiGameManager.socketGameSend.confirmBuildRoad(index);
                this.uiGameManager.socketGameSend.confirmBuildShip(index);
                break;
            case ActionState.PlaceShip:
            case ActionState.PlaceShipForFree:
                this.uiGameManager.socketGameSend.confirmBuildShip(index);
                break;
            case ActionState.SelectShipToMove:
                this.uiGameManager.socketGameSend.selectedShipToMove(index);
                break;
            case ActionState.MoveShip:
                this.uiGameManager.socketGameSend.confirmMoveShip(index);
                break;
            case ActionState.SelectRoadToRemove:
                this.uiGameManager.socketGameSend.confirmRemoveRoad(index);
                break;
            default:
                break;
        }
    }
    highlightEdge(edge, index) {
        const highlightParameters = this.getEdgeHighlightParameters(edge, index);
        const action = this.getActionForEdgeHighlight(highlightParameters, index);
        this.highlight(highlightParameters, action);
    }
    getConfirmationParameters(index) {
        const confirmationParameters = [];
        if (ArrayUtils.elementInArray(this.roadEdges, index)) {
            confirmationParameters.push({
                texture: UIGameSpriteHandler.getRoadForPlayer(this.uiGameManager.gameController.myColor),
                canTakeAction: true,
                bankActionAmount: undefined,
                action: (placementIndex)=>this.uiGameManager.socketGameSend.confirmBuildRoad(placementIndex),
                resourcesToBuild: []
            });
        }
        if (ArrayUtils.elementInArray(this.shipEdges, index)) {
            confirmationParameters.push({
                texture: UIGameSpriteHandler.getShipForPlayer(this.uiGameManager.gameController.myColor, TileEdgeDirection.NorthWest),
                canTakeAction: true,
                bankActionAmount: undefined,
                action: (placementIndex)=>this.uiGameManager.socketGameSend.confirmBuildShip(placementIndex),
                resourcesToBuild: []
            });
        }
        return confirmationParameters;
    }
    constructor(uiGameManager){
        super(uiGameManager);
        this.roadEdges = [];
        this.shipEdges = [];
    }
}
